// cesium-polygon.ts - Manages polygon drawing functionality

import {
  Cartesian3,
  Cartographic,
  Math as CesiumMath,
  ClassificationType,
  ClippingPolygon,
  ClippingPolygonCollection,
  Color,
  PolygonHierarchy,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
} from "cesium";

// Polygon drawing state
let isDrawingPolygon = false;
let polygonPoints: any[] = [];
let activePolygonEntity: any = null;

// Force immediate scene refresh (assuming this utility is available or moved)
// You might need to pass the viewer instance to this function if it's not global
declare function forceImmediateRender(viewer: any): void;

// Handle polygon drawing clicks
function handlePolygonClick(cesiumViewer: any, event: any) {
  let position = null;
  position = cesiumViewer.scene.pickPosition(event.position);
  if (!position) {
    position = cesiumViewer.camera.pickEllipsoid(
      event.position,
      cesiumViewer.scene.globe.ellipsoid,
    );
  }
  if (!position) {
    const globe = cesiumViewer.scene.globe;
    if (globe) {
      position = globe.pick(
        cesiumViewer.camera.getPickRay(event.position),
        cesiumViewer.scene,
      );
    }
  }

  if (position) {
    addPolygonPoint(cesiumViewer, position);
  } else {
  }
}

// Add a point to the current polygon
function addPolygonPoint(cesiumViewer: any, position: any) {
  if (
    !position ||
    !Number.isFinite(position.x) ||
    !Number.isFinite(position.y) ||
    !Number.isFinite(position.z)
  ) {
    return;
  }

  const cartographic = Cartographic.fromCartesian(position);
  const longitude = CesiumMath.toDegrees(cartographic.longitude);
  const latitude = CesiumMath.toDegrees(cartographic.latitude);
  const actualHeight = cartographic.height;
  const actualPosition = Cartesian3.fromDegrees(
    longitude,
    latitude,
    actualHeight,
  );

  polygonPoints.push(actualPosition);


  if (!activePolygonEntity) {
    activePolygonEntity = cesiumViewer.entities.add({
      name: "Drawing Polygon",
      polygon: {
        hierarchy: new PolygonHierarchy(polygonPoints),
        material: Color.CYAN.withAlpha(0.7),
        outline: true,
        outlineColor: Color.CYAN,
        outlineWidth: 2,
        perPositionHeight: true,
        extrudedHeight: 1,
        classificationType: ClassificationType.CESIUM_3D_TILE,
      },
    });
  } else {
    activePolygonEntity.polygon.hierarchy = new PolygonHierarchy(polygonPoints);
  }

  forceImmediateRender(cesiumViewer);
}

// Complete the current polygon
function completePolygon(cesiumViewer: any) {
    if (polygonPoints.length < 3) {
      alert("A polygon needs at least 3 points. Left-click to add more points.");
      return;
    }
  
  
    if (activePolygonEntity) {
      activePolygonEntity.polygon.material = Color.GREEN.withAlpha(0.8);
      activePolygonEntity.polygon.outlineColor = Color.DARKGREEN;
      activePolygonEntity.polygon.outlineWidth = 3;
      activePolygonEntity.polygon.perPositionHeight = true;
      activePolygonEntity.polygon.extrudedHeight = 1;
      activePolygonEntity.polygon.classificationType = ClassificationType.CESIUM_3D_TILE;
      activePolygonEntity.name = `3D Polygon (${polygonPoints.length} points)`;
  
      const coordinates = polygonPoints.map((point) => {
        const cartographic = Cartographic.fromCartesian(point);
        return [
          CesiumMath.toDegrees(cartographic.longitude),
          CesiumMath.toDegrees(cartographic.latitude),
        ];
      });
      coordinates.push(coordinates[0]);
  
      const geoJsonFeature = {
        type: "Feature",
        properties: { name: `Polygon (${polygonPoints.length} points)` },
        geometry: { type: "Polygon", coordinates: [coordinates] },
      };
  
  
      const googlePhotorealisticTileset = (window as any).googlePhotorealisticTileset;
      if (googlePhotorealisticTileset) {
        const ellipsoidPositions = polygonPoints.map((point) => {
          const cartographic = Cartographic.fromCartesian(point);
          return Cartesian3.fromRadians(cartographic.longitude, cartographic.latitude, 0);
        });
  
        const clippingPolygons = new ClippingPolygonCollection({
          polygons: [new ClippingPolygon({ positions: ellipsoidPositions })],
          enabled: true,
          inverse: true,
        });
        googlePhotorealisticTileset.clippingPolygons = clippingPolygons;
        forceImmediateRender(cesiumViewer);
      }
  
      window.dispatchEvent(
        new CustomEvent("polygonCompleted", {
          detail: {
            entity: activePolygonEntity,
            geoJson: geoJsonFeature,
            points: polygonPoints.length,
            clippingApplied: !!googlePhotorealisticTileset,
          },
        }),
      );
    }
  
    isDrawingPolygon = false;
    polygonPoints = [];
    activePolygonEntity = null;
    forceImmediateRender(cesiumViewer);
  }
  

// Stop polygon drawing mode
function stopPolygonDrawing(cesiumViewer: any) {
  isDrawingPolygon = false;
  polygonPoints = [];
  if (activePolygonEntity && cesiumViewer) {
    cesiumViewer.entities.remove(activePolygonEntity);
    activePolygonEntity = null;
  }
}

// Initialize polygon drawing functionality
export function initializePolygonDrawing(cesiumViewer: any) {
  if (!cesiumViewer) {
    return;
  }
}

// Start polygon drawing mode
export function startPolygonDrawing(cesiumViewer: any) {
  if (!cesiumViewer) {
    return;
  }
  isDrawingPolygon = true;
  polygonPoints = [];
  if (activePolygonEntity) {
    cesiumViewer.entities.remove(activePolygonEntity);
    activePolygonEntity = null;
  }
}

// Public function to be called from the event handler
export function onPolygonClick(cesiumViewer: any, event: any) {
  if (isDrawingPolygon) {
    handlePolygonClick(cesiumViewer, event);
  }
}

export function onPolygonRightClick(cesiumViewer: any) {
    if (isDrawingPolygon) {
      completePolygon(cesiumViewer);
    }
}
  
export { stopPolygonDrawing }; 